import { Alert, Button, EmptyState, LoadingState, Tabs, useToast } from '@orbind/ui'
import { useEffect, useMemo, useState } from 'react'
import { type TemplateKind, type TemplateResumo, useExcluirTemplate, useTemplates } from './template-data.ts'
import { TemplateNoItemPage } from './TemplateNoItemPage.tsx'
import './templates.css'

/**
 * Biblioteca de templates (SPEC-20260830-1225).
 *
 * Duas abas: **Itens de Trabalho** (a cadeia inteira de um item) e
 * **Checklists** (uma lista cujas linhas podem virar Itens de Trabalho).
 *
 * Os DOIS tipos editam na mesma tela do Item de Trabalho (bastidor): o de
 * item desde a SPEC-20260830-1530; o de checklist desde a
 * SPEC-20261104-1550 (WI-0312) — o formulário à parte não deixava a linha
 * que vira item ter checklist, campos nem netos. Agora a linha convertida é
 * um Item de Trabalho completo, recursivamente.
 */

export function TemplatesSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const [aba, setAba] = useState<TemplateKind>('work_item')
  const [editando, setEditando] = useState<TemplateResumo | null>(null)
  /** Criando um template do zero — da natureza da aba. */
  const [criando, setCriando] = useState<TemplateKind | null>(null)

  const lista = useTemplates()
  const excluir = useExcluirTemplate()
  const toast = useToast()

  // "+" do explorer: cria um template de CHECKLIST (o de item costuma nascer
  // dentro do proprio Item de Trabalho, pelo "Salvar como template")
  useEffect(() => {
    if (criarSinal > 0) {
      setAba('checklist')
      setCriando('checklist')
    }
  }, [criarSinal])

  const doTipo = useMemo(
    () => (lista.data?.templates ?? []).filter((t) => t.kind === aba),
    [lista.data, aba],
  )

  // os dois tipos abrem na MESMA tela do Item de Trabalho
  if (editando) return <TemplateNoItemPage template={editando} onFechar={() => setEditando(null)} />
  if (criando) return <TemplateNoItemPage template={null} kind={criando} onFechar={() => setCriando(null)} />

  const rotuloNovo = aba === 'checklist' ? 'Novo template de checklist' : 'Novo template de item'

  return (
    <div className="tpl">
      <div className="tpl__topo">
        <Tabs
          items={[
            { id: 'work_item', label: 'Itens de Trabalho' },
            { id: 'checklist', label: 'Checklists' },
          ]}
          value={aba}
          onChange={(v) => setAba(v as TemplateKind)}
        />
        <Button variant="primary" onClick={() => setCriando(aba)}>
          <i className="fas fa-plus" aria-hidden /> {rotuloNovo}
        </Button>
      </div>

      <p className="tpl__ajuda">
        {aba === 'work_item'
          ? 'Modelos da cadeia inteira de um Item de Trabalho — filhos, checklists, campos complementares, membros e etiquetas. Crie um aqui do zero, ou abra um item pronto e use “Salvar como template”.'
          : 'Modelos de checklist, montados na tela do Item de Trabalho. Uma linha convertida em item leva tudo que um Item tem — inclusive checklists próprios com linhas que também viram itens.'}
      </p>

      {lista.isLoading ? <LoadingState label="Carregando templates…" /> : null}
      {lista.error ? <Alert tone="danger">Não foi possível carregar os templates.</Alert> : null}

      {!lista.isLoading && doTipo.length === 0 ? (
        <EmptyState
          icon="fas fa-clone"
          title={aba === 'work_item' ? 'Nenhum template de item ainda' : 'Nenhum template de checklist ainda'}
          description={
            aba === 'work_item'
              ? 'Crie um aqui do zero, ou abra um Item de Trabalho bem montado e use “Salvar como template” para reaproveitar a estrutura dele.'
              : 'Crie o primeiro template de checklist para reaproveitar listas que se repetem.'
          }
          action={
            <Button variant="primary" onClick={() => setCriando(aba)}>
              {rotuloNovo}
            </Button>
          }
        />
      ) : null}

      {doTipo.length > 0 ? (
        <div className="tpl__lista">
          {doTipo.map((t) => (
            <div key={t.id} className="tpl__card">
              <i className={`tpl__card-ico ${t.kind === 'work_item' ? 'fas fa-sitemap' : 'fas fa-list-check'}`} aria-hidden />
              <div className="tpl__card-txt">
                <button type="button" className="tpl__card-nome" onClick={() => setEditando(t)}>
                  {t.name}
                </button>
                {t.description ? <p className="tpl__card-desc">{t.description}</p> : null}
              </div>
              {t.kind === 'work_item' ? (
                <span className="tpl__card-tag" title="Como o template nasce ao ser aplicado">
                  {t.config.resetStatus === false ? 'Mantém status' : 'Status inicial'}
                </span>
              ) : null}
              <button
                type="button"
                className="tpl__card-btn"
                title="Editar"
                aria-label={`Editar ${t.name}`}
                onClick={() => setEditando(t)}
              >
                <i className="fas fa-pen" aria-hidden />
              </button>
              <button
                type="button"
                className="tpl__card-btn tpl__card-btn--perigo"
                title="Excluir"
                aria-label={`Excluir ${t.name}`}
                onClick={() => {
                  if (!confirm(`Excluir o template “${t.name}”? Os itens já criados a partir dele não são afetados.`)) return
                  excluir.mutate(t.id, {
                    onSuccess: () => toast.push({ tone: 'success', message: 'Template excluído.' }),
                  })
                }}
              >
                <i className="fas fa-trash" aria-hidden />
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}
